import { Organization, OrganizationSubType } from './organization.entity';

type FieldConfigKey = OrganizationSubType;
type FieldConfig = { [key in FieldConfigKey]: (keyof Organization)[] };

const baseEntityFields: (keyof Organization)[] = [
  'id',
  'type',
  'createdAt',
  'updatedAt',
];

/**
 * Organization 6 and 7 are nearly completely private, so they are handled
 * separately from the other organization types.
 */
// organization 6
const publicFieldsSelfHelpAddressOther: (keyof Organization)[] = [
  ...baseEntityFields,
  'name',
  'subType',
  'additionalTopics',
];

/**
 * From here on, the configurations and const namings only refer to organization 1 - 5.
 */
// public fields, that are available for all organization types
const universalPublicFields: (keyof Organization)[] = [
  ...baseEntityFields,
  'subType',
  'name',
  'addressFederalState',
  'websites',
  'socialMediaLinks',
];

/**
 * Fields anonymous callers may search and filter by (for the public subtypes 1 - 5) but never receive.
 * legacyId: redirected legacy nakos.de address links (idx@<legacyId>) resolve through the public
 * search and the /web/addresses/:legacyId landing route without login.
 */
export const publicSearchOnlyFields: (keyof Organization)[] = ['legacyId'];

// don't insert into red config
const publicFieldsForAllButRed: (keyof Organization)[] = [
  'topics',
  'additionalTopics',
];

// don't insert into green AND blue config
const publicFieldsForAllButGreenAndBlue: (keyof Organization)[] = [
  'coverageArea',
];

// don't insert into yellow config
const publicFieldsForAllButYellow: (keyof Organization)[] = [
  'addressCo',
  'addressStreetAndNumber',
  'addressPostalCode',
  'addressCity',
  'phoneContacts',
  'emails',
  'contactPersons', // !!! only the first contactPerson should be public
];

// don't insert into blue config
const publicFieldsForAllButBlue: (keyof Organization)[] = [
  'subTypeForm',
  'foundingYear',
];

/**
 * The following const's define the final public fields for each organization type.
 * They are built using the above defined const's to avoid redundancy.
 */
// public fields that are only available in organization 1 (red)
const publicFieldsRa: (keyof Organization)[] = [
  ...universalPublicFields,
  ...publicFieldsForAllButGreenAndBlue,
  ...publicFieldsForAllButYellow,
  ...publicFieldsForAllButBlue,
  'workingMethod',
  'isMainOffice',
  'mainOffice',
  'branchOffices',
  'isBarrierFree',
];

// public fields that are only available in organization 2 (green)
const publicFieldsGa: (keyof Organization)[] = [
  ...universalPublicFields,
  ...publicFieldsForAllButRed,
  ...publicFieldsForAllButYellow,
  ...publicFieldsForAllButBlue,
  'headquarters',
  'stateLevelSubdivisions',
  'hasTransparencyLabel',
  'transparencyPdfId',
  'transparencyPdf'
];

// public fields that are available in both organizations 3 & 4 (yellow)
const publicFieldsYa: (keyof Organization)[] = [
  ...universalPublicFields,
  ...publicFieldsForAllButRed,
  ...publicFieldsForAllButGreenAndBlue,
  ...publicFieldsForAllButBlue,
  'meetingPostalCode',
  'meetingLocation',
  'groupStatus',
  'memberCount',
  'targetGroup',
  'description',
  'fileId',
  'file',
  'meetingFormat',
  'toolUsed',
  'dataProtectionCompliant',
  'language',
  'localContactPoint',
  'contactOrganization',
];

// public fields that are only available in organization 3 (yellow)
const publicFieldsYa3: (keyof Organization)[] = [
  ...publicFieldsYa,
  'provider',
];

// public fields that are only available in organization 4 (yellow)
const publicFieldsYa4: (keyof Organization)[] = [
  ...publicFieldsYa,
];

// public fields that are only available in organization 5 (blue)
const publicFieldsBa: (keyof Organization)[] = [
  ...universalPublicFields,
  ...publicFieldsForAllButRed,
  ...publicFieldsForAllButYellow,
  'contactOrganization',
  'problemExplanation',
  'specificIssues',
  'affectedPerson',
  'dateOfBirth',
  'interestedInfos',
  'experienceExchangeTopics',
  'foundingInterests',
  'contactInterests',
];


export const publicFieldsConfig: FieldConfig = {
  selfHelpAddressRa: publicFieldsRa,
  selfHelpAddressGa: publicFieldsGa,
  selfHelpAddressCommunity: publicFieldsYa3,
  selfHelpAddressShGroup: publicFieldsYa4,
  selfHelpAddressBa: publicFieldsBa,
  selfHelpAddressOther: publicFieldsSelfHelpAddressOther,
};

/**
 * Fields anonymous callers may filter by for each public organization subtype.
 * Returned fields and filterable fields deliberately have different rules.
 */
export const publicFilterFieldsConfig: Partial<Record<OrganizationSubType, (keyof Organization)[]>> = {
  selfHelpAddressBa: [],
  selfHelpAddressCommunity: ['targetGroup', 'language'],
  selfHelpAddressGa: ['stateLevelSubdivisions', 'hasTransparencyLabel'],
  selfHelpAddressRa: ['subTypeForm', 'addressFederalState'],
  selfHelpAddressShGroup: ['targetGroup', 'language'],
};

// Accepted from public deep links, but deliberately not shown in the filter modal.
export const PUBLIC_ORGANIZATION_DEEP_LINK_FILTERS = {
  topics: 'relation-id',
} as const;

// Share the public list boundary with topic publication checks.
export const PUBLIC_ORGANIZATION_LIST_SUB_TYPES: OrganizationSubType[] = [
  'selfHelpAddressRa',
  'selfHelpAddressGa',
  'selfHelpAddressCommunity',
  'selfHelpAddressShGroup',
  'selfHelpAddressBa',
];

export const PUBLIC_TOPIC_ORGANIZATION_SUB_TYPES = PUBLIC_ORGANIZATION_LIST_SUB_TYPES
  .filter(subType => publicFieldsConfig[subType].includes('topics'));
